import axios, { AxiosResponse, AxiosError } from "axios";
import { getCookie } from "@util/cookie";
import {
  ContractOffer,
  ContractRespondRequest,
  CreateContractRequest,
  CreateContractResponse,
  HireResponse,
} from "@type/contract";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

export const getMyContractOffers = async (
  status?: string
): Promise<ContractOffer[]> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/contracts/my-offers`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        status: status,
      },
      timeout: 10000, // 10 second timeout
    });
    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    throw new Error(axiosError.message);
  }
};

export const contractRespond = async (
  body: ContractRespondRequest
): Promise<boolean> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const formData = new FormData();
    formData.append("status_contract", body.status);
    if (body.decline_reason) {
      formData.append("decline_reason", body.decline_reason);
    }
    if (body.message_to_company) {
      formData.append("message_to_company", body.message_to_company);
    }
    if (body.signed_contract && body.signed_contract instanceof File) {
      formData.append("signed_contract", body.signed_contract);
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/contracts/${body.contractId}/respond`,
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: formData,
      timeout: 10000, // 10 second timeout
    });

    return response.data.success;
  } catch (error) {
    const axiosError = error as AxiosError;
    throw new Error(axiosError.message);
  }
};

export const getContractById = async (
  contractId: string
): Promise<ContractOffer> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/contracts/${contractId}`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 10000, // 10 second timeout
    });

    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    throw new Error(axiosError.message);
  }
};

export const createContract = async (
  body: CreateContractRequest
): Promise<CreateContractResponse> => {
  try {
    const token = getCookie("authToken");
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const formData = new FormData();
    formData.append("application_id", body.application_id || "");
    formData.append("job_id", body.job_id);
    formData.append("start_date", body.start_date);
    if (body.end_date !== "") {
      formData.append("end_date", body.end_date || "");
    }
    if (body.cc !== "") {
      formData.append("cc_email", body.cc || "");
    }
    formData.append("salary", body.salary.toString());
    formData.append("salary_currency", body.salary_currency);
    formData.append("payment_period", body.payment_period);
    formData.append("benefits", body.benefit);
    formData.append("work_option", body.work_option);
    formData.append("contract_terms", body.contract_terms);
    formData.append("use_template_email", body.use_template_email.toString());
    formData.append("email_subject", body.email_subject);
    formData.append("email_body", body.email_body);

    if (body.attachment && body.attachment instanceof File) {
      formData.append("attachment", body.attachment);
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/contracts/create`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: formData,
      timeout: 12000, // 12 second timeout
    });

    return {
      code: response.data.code,
      message: response.data.message,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      code: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Request timed out"
          : "Failed to send invitation"),
    };
  }
};

export const getMyHires = async (status?: string): Promise<HireResponse[]> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/contracts/ongoing-jobs`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        ongoing_jobs_status: status,
      },
      timeout: 10000, // 10 second timeout
    });
    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    throw new Error(axiosError.message);
  }
};

export const createContractV2 = async (
  body: CreateContractRequest
): Promise<any> => {
  const token = getCookie("authToken");
  const formData = new FormData();
  formData.append("application_ids", body.application_ids.join(","));
  formData.append("job_id", body.job_id);
  formData.append("start_date", body.start_date);
  if (body.end_date !== "") {
    formData.append("end_date", body.end_date || "");
  }
  if (body.cc !== "") {
    formData.append("cc_email", body.cc || "");
  }
  formData.append("reply_to", body.reply_to);
  formData.append("sender_name", body.sender_name);
  formData.append("salary", body.salary.toString());
  formData.append("salary_currency", body.salary_currency);
  formData.append("payment_period", body.payment_period);
  formData.append("benefits", body.benefit);
  formData.append("work_option", body.work_option);
  formData.append("contract_terms", body.contract_terms);
  formData.append("use_template_email", body.use_template_email.toString());
  formData.append("email_subject", body.email_subject);
  formData.append("email_body", body.email_body);
  formData.append("offer_notes", body.offer_notes || "");

  if (body.attachment && body.attachment instanceof File) {
    formData.append("attachment", body.attachment);
  }

  return await axios({
    url: `${apiURL}/contracts/v2/create`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  });
};